import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy/client';
import { env } from '../env';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNestedValue = (obj: any, path: string[]) => {
  let value = obj;
  for (let i = 0; i < path.length; i++) {
    if (value == undefined) return undefined;
    value = value[path[i]];
  }
  return value;
};

export const getBase64 = (file: Blob): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    if (!(file instanceof Blob)) {
      reject({ error: 'File is not a blob' });
    }

    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    reader.onload = (e) => {
      const binaryString = e.target?.result;
      if (binaryString && typeof binaryString === 'string') {
        resolve(window.btoa(binaryString));
      }
    };

    reader.onerror = (error) => reject(error);

    if (rABS) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** The signed-in caller's bearer token, as the generated client holds it. */
async function authHeaders(body?: unknown): Promise<Record<string, string>> {
  const config = apiClient.request.config;
  const tokenSource = config.TOKEN;
  const token =
    typeof tokenSource === 'function'
      ? await (tokenSource as (options: unknown) => Promise<string> | string)({})
      : (tokenSource ?? '');

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  return headers;
}

/**
 * A failed v2 call, with the error envelope the backend sends: `code` is what
 * the client may branch on, `message` is written for the reader.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor({ status, code, message }: { status: number; code: string; message: string }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** Not every failure comes from the API — a gateway or a proxy answers in its
 * own shape, and then there is nothing to read out. */
const apiErrorFrom = async (response: Response): Promise<ApiError> => {
  const body = await response.text();

  try {
    const envelope = JSON.parse(body) as { message?: string; code?: string };

    if (envelope?.message) {
      return new ApiError({
        status: response.status,
        code: envelope.code ?? '',
        message: envelope.message
      });
    }
  } catch {
    // Not JSON, so the status and the raw body are all there is.
  }

  return new ApiError({
    status: response.status,
    code: '',
    message: `Request failed (${response.status}): ${body}`
  });
};

/** A v2 response that is a file rather than JSON.

 `requestBinary` cannot serve these: it builds on `config.BASE`, which carries
 the generated client's /api/v1 prefix. */
export async function requestBlob({ path }: { path: string }): Promise<Blob> {
  const config = apiClient.request.config;
  const response = await fetch(`${new URL(config.BASE).origin}${path}`, {
    headers: await authHeaders()
  });

  if (!response.ok) {
    throw await apiErrorFrom(response);
  }

  return response.blob();
}

/**
 * Authenticated JSON call against a path the generated legacy client does not
 * cover — today the /api/v2 company routes, which the legacy backend forwards
 * to the new backend. Reuses the session the dashboard already holds; the
 * generated v2 client keeps its own token store and is not logged in here.
 */
export async function requestJson<T>({
  path,
  method = 'GET',
  body
}: {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}): Promise<T> {
  const config = apiClient.request.config;

  // config.BASE already carries the /api/v1 prefix of the generated client,
  // so a v2 path has to hang off the origin.
  const response = await fetch(`${new URL(config.BASE).origin}${path}`, {
    method,
    headers: await authHeaders(body),
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (!response.ok) {
    throw await apiErrorFrom(response);
  }

  return (await response.json()) as T;
}

/** A v2 write that answers 204 and carries no body to parse. */
export async function requestVoid({
  path,
  method = 'POST',
  body
}: {
  path: string;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}): Promise<void> {
  const config = apiClient.request.config;

  const response = await fetch(`${new URL(config.BASE).origin}${path}`, {
    method,
    headers: await authHeaders(body),
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (!response.ok) {
    throw await apiErrorFrom(response);
  }
}

/** A v2 upload. The Content-Type is left unset on purpose: the browser adds
 it together with the multipart boundary, and setting it drops the boundary. */
export async function requestMultipart({
  path,
  form
}: {
  path: string;
  form: FormData;
}): Promise<void> {
  const config = apiClient.request.config;

  const response = await fetch(`${new URL(config.BASE).origin}${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: form
  });

  if (!response.ok) {
    throw new Error(`Request failed (${response.status}): ${await response.text()}`);
  }
}

export async function requestBinary({
  path,
  method = 'POST',
  body
}: {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}): Promise<Blob> {
  const config = apiClient.request.config;
  const tokenSource = config.TOKEN;
  const token =
    typeof tokenSource === 'function'
      ? await (tokenSource as (options: unknown) => Promise<string> | string)({})
      : (tokenSource ?? '');

  const url = `${config.BASE}${path}`;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const errorBody = await response.text();
      if (errorBody) detail = errorBody;
    } catch (e) {
      console.error(e);
    }
    throw new Error(`Request failed (${response.status}): ${detail}`);
  }

  if (response.status === 204) {
    return new Blob();
  }
  return response.blob();
}

class PxtraApiClientSingleton {
  private static instance: PxtraApiClientLegacy;

  private constructor() {}

  public static getInstance(): PxtraApiClientLegacy {
    if (!PxtraApiClientSingleton.instance) {
      PxtraApiClientSingleton.instance = new PxtraApiClientLegacy({
        config: {
          BASE: env('API_BASE_URL')
        }
      });
    }
    return PxtraApiClientSingleton.instance;
  }
}

export const apiClient = PxtraApiClientSingleton.getInstance();
